import { useMutation } from "@apollo/client";
import { showNotification, updateNotification } from "@mantine/notifications";
import { FC, Fragment } from "react";
import { SubmitHandler, useForm, DefaultValues } from "react-hook-form";
import { useNavigate, useSearch } from "react-location";
import { resetPassword } from "../../graphql/mutations/auth";
import { usePage } from "../../hooks/usePage";

interface ResetPasswordForm {
  email: string;
  otp: string;
  newPassword: string;
  confirmPassword: string;
}

const ResetPasswordScreen: FC = () => {
  usePage("Kava | Reset Password");
  const navigate = useNavigate();
  const search = useSearch();

  const {
    register,
    formState: { errors, isValid },
    handleSubmit,
    watch,
  } = useForm<ResetPasswordForm>({ 
    defaultValues: {
      email: (search as { email?: string }).email || "",
    } as DefaultValues<ResetPasswordForm>,
    mode: "onChange",
  });

  const newPassword = watch("newPassword", "");

  const [executePasswordReset, { loading }] = useMutation(resetPassword);

  const onSubmit: SubmitHandler<ResetPasswordForm> = (data) => {
    if (data.newPassword !== data.confirmPassword) {
      showNotification({
        id: "mismatch",
        title: "Validation Error",
        message: "New password and confirmation must match.",
        color: "red",
      });
      return;
    }

    showNotification({
      id: "reset-password",
      loading: true,
      title: "Setting New Password",
      message: "Updating your password...",
      autoClose: false,
      disallowClose: true,
    });

    executePasswordReset({
      variables: {
        input: {
          email: data.email,
          token: data.otp,
          password: data.newPassword,
          password_confirmation: data.newPassword,
        },
      },
    })
      .then((res) => {
        const result = res.data?.resetPassword;
        if (result?.status) {
          updateNotification({
            id: "reset-password",
            title: "Success",
            message:
              result.message ||
              "Your password has been reset successfully. Please log in.",
            color: "blue",
          });
          navigate({ to: "/cpanel-login", replace: true });
        } else {
          updateNotification({
            id: "reset-password",
            title: "Failed",
            message:
              result?.message ||
              "Password reset failed. Please check your email and OTP.",
            color: "red",
          });
        }
      })
      .catch((error) => {
        updateNotification({
          id: "reset-password",
          title: "Error",
          message:
            error.message || "An unexpected error occurred. Please try again.",
          color: "red",
        });
      });
  };

  return (
    <Fragment>
      <div className="flex flex-col items-center">
        <h2 className="mt-6 text-3xl font-medium text-gray-900">
          Reset Your Password
        </h2>
        <p className="mt-2 text-sm text-gray-600">
          Enter the token you received, and your new password.
        </p>
      </div>
      <div className="mt-8 max-w-sm w-full">
        <div className="mt-6">
          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label
                htmlFor="otp"
                className="block text-sm font-medium text-gray-700"
              >
                Verification Token (OTP)
              </label>
              <div className="mt-1">
                <input
                  type="text"
                  {...register("otp", {
                    required: "Token/OTP is required",
                    minLength: {
                      value: 4,
                      message: "Token is 4 characters",
                    },
                  })}
                  id="otp"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
                  placeholder="Enter the 4-digit code or reset token"
                />
                {errors?.otp && (
                  <p className="text-red-400 text-sm mt-1">
                    {errors?.otp?.message}
                  </p>
                )}
              </div>
            </div>
            <div>
              <label
                htmlFor="newPassword"
                className="block text-sm font-medium text-gray-700"
              >
                New Password
              </label>
              <div className="mt-1">
                <input
                  type="password"
                  {...register("newPassword", {
                    required: "New Password is required",
                    minLength: {
                      value: 8,
                      message: "Password must be at least 8 characters",
                    },
                  })}
                  id="newPassword"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
                  placeholder="New Password (min 8 characters)"
                />
                {errors?.newPassword && (
                  <p className="text-red-400 text-sm mt-1">
                    {errors?.newPassword?.message}
                  </p>
                )}
              </div>
            </div>
            <div>
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-700"
              >
                Confirm New Password
              </label>
              <div className="mt-1">
                <input
                  type="password"
                  {...register("confirmPassword", {
                    required: "Confirmation is required",
                    validate: (value) =>
                      value === newPassword || "Passwords do not match",
                  })}
                  id="confirmPassword"
                  className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm p-2 border"
                  placeholder="Confirm New Password"
                />
                {errors?.confirmPassword && (
                  <p className="text-red-400 text-sm mt-1">
                    {errors?.confirmPassword?.message}
                  </p>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={loading || !isValid}
                className="w-full flex justify-center py-2 px-4 border bg-white rounded-md shadow-sm text-sm font-medium text-green-600 hover:text-white border-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-300 disabled:border-gray-300 disabled:text-gray-500 transition duration-200"
              >
                Set New Password
                {loading && (
                  <svg
                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </Fragment>
  );
};

export default ResetPasswordScreen;
